// Google API Key - will be set by the app
let GOOGLE_API_KEY = '';

// Function to set the API key (call this from your app initialization)
export const setGoogleApiKey = (key) => {
    GOOGLE_API_KEY = key;
};

// Function to get API key (for debugging)
export const getGoogleApiKey = () => GOOGLE_API_KEY;

const normalizeCity = (value) => String(value || '').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
    .replace(/^ville de\s+/, '').replace(/\s+city$/, '');

const configuredCities = (value) => {
    const entries = Array.isArray(value) ? value : String(value || '').split(/[,;\n]/);
    return Array.from(new Set(entries.map(normalizeCity).filter(Boolean)));
};

const predictionInServiceArea = (prediction, cities) => {
    if (!cities.length) return true;
    const terms = Array.isArray(prediction.terms)
        ? prediction.terms.map(term => normalizeCity(term && term.value)) : [];
    const descriptionSegments = String(prediction.description || '').split(',').map(normalizeCity);
    return cities.some(city => terms.includes(city) || descriptionSegments.includes(city));
};

export const fetchPlacesAutocomplete = (searchKeyword, sessionToken, restrictCountry = '', locationBias = null, serviceCities = '') => {
    return new Promise((resolve, reject) => {
        if (!GOOGLE_API_KEY) {
            reject("Google API Key not set. Call setGoogleApiKey() first.");
            return;
        }

        let url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(searchKeyword)}&key=${GOOGLE_API_KEY}`;

        if (sessionToken) {
            url += `&sessiontoken=${sessionToken}`;
        }

        if (restrictCountry) {
            url += `&components=country:${restrictCountry}`;
        }
        if (locationBias && Number.isFinite(Number(locationBias.lat)) &&
            Number.isFinite(Number(locationBias.lng))) {
            url += `&location=${Number(locationBias.lat)},${Number(locationBias.lng)}&radius=50000`;
        }

        fetch(url)
            .then(response => response.json())
            .then(json => {
                if (json.status === 'OK' || json.status === 'ZERO_RESULTS') {
                    const cities = configuredCities(serviceCities);
                    const results = json.predictions ? json.predictions
                    .filter(prediction => predictionInServiceArea(prediction, cities))
                    .map(prediction => ({
                        description: prediction.description,
                        place_id: prediction.place_id,
                        structured_formatting: prediction.structured_formatting
                    })) : [];
                    resolve(results);
                } else {
                    console.log('Places Autocomplete Error:', json.status, json.error_message);
                    reject(json.error_message || json.status);
                }
            })
            .catch(error => {
                console.log('fetchPlacesAutocomplete Error:', error);
                reject("fetchPlacesAutocomplete Call Error");
            });
    });
}

export const fetchCoordsfromPlace = (place_id) => {
    return new Promise((resolve, reject) => {
        if (!GOOGLE_API_KEY) {
            reject("Google API Key not set. Call setGoogleApiKey() first.");
            return;
        }

        const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${place_id}&fields=geometry&key=${GOOGLE_API_KEY}`;

        fetch(url)
            .then(response => response.json())
            .then(json => {
                if (json.status === 'OK' && json.result && json.result.geometry) {
                    resolve({
                        lat: json.result.geometry.location.lat,
                        lng: json.result.geometry.location.lng
                    });
                } else {
                    console.log('Place Details Error:', json.status, json.error_message);
                    reject(json.error_message || json.status);
                }
            })
            .catch(error => {
                console.log('fetchCoordsfromPlace Error:', error);
                reject("fetchCoordsfromPlace Call Error");
            });
    });
}


export const fetchAddressfromCoords = (latlng) => {
    return new Promise((resolve, reject) => {
        if (!GOOGLE_API_KEY) {
            reject("Google API Key not set. Call setGoogleApiKey() first.");
            return;
        }

        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latlng}&key=${GOOGLE_API_KEY}`;

        fetch(url)
            .then(response => response.json())
            .then(json => {
                if (json.status === 'OK' && json.results && json.results.length > 0) {
                    resolve(json.results[0].formatted_address);
                } else if (json.status === 'ZERO_RESULTS') {
                    resolve('Unknown Location');
                } else {
                    console.log('Reverse Geocode Error:', json.status, json.error_message);
                    reject(json.error_message || json.status);
                }
            })
            .catch(error => {
                console.log('fetchAddressfromCoords Error:', error);
                reject("fetchAddressfromCoords Call Error");
            });
    });
}

export const getDistanceMatrix = (startLoc, destLoc) => {
    return new Promise((resolve, reject) => {
        if (!GOOGLE_API_KEY) {
            reject("Google API Key not set. Call setGoogleApiKey() first.");
            return;
        }

        const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${startLoc}&destinations=${destLoc}&key=${GOOGLE_API_KEY}`;

        fetch(url)
            .then(response => response.json())
            .then(json => {
                if (json.status === 'OK' && json.rows && json.rows[0] && Array.isArray(json.rows[0].elements)) {
                    resolve(json.rows[0].elements.map(element => element.status === 'OK' ? {
                        found: true,
                        distance_in_km: element.distance.value / 1000,
                        time_in_secs: element.duration.value,
                        timein_text: element.duration.text,
                        distance: element.distance,
                        duration: element.duration
                    } : {
                        found: false,
                        status: element.status
                    }));
                } else {
                    console.log('Distance Matrix Error:', json.status, json.error_message);
                    reject(json.error_message || json.status);
                }
            })
            .catch(error => {
                console.log('getDistanceMatrix Error:', error);
                reject("getDistanceMatrix Call Error");
            });
    });
}

export const getDirectionsApi = (startLoc, destLoc, waypoints) => {
    return new Promise((resolve, reject) => {
        if (!GOOGLE_API_KEY) {
            reject("Google API Key not set. Call setGoogleApiKey() first.");
            return;
        }

        let url = `https://maps.googleapis.com/maps/api/directions/json?origin=${startLoc}&destination=${destLoc}&key=${GOOGLE_API_KEY}`;

        if (waypoints) {
            url += `&waypoints=${waypoints}`;
        }

        fetch(url)
            .then(response => response.json())
            .then(json => {
                if (json.status === 'OK' && json.routes && json.routes.length > 0) {
                    const route = json.routes[0];
                    const leg = route.legs[0];

                    // Calculate total distance and duration for all legs
                    let totalDistance = 0;
                    let totalDuration = 0;

                    route.legs.forEach(leg => {
                        totalDistance += leg.distance.value;
                        totalDuration += leg.duration.value;
                    });

                    resolve({
                        distance_in_km: totalDistance / 1000,
                        time_in_secs: totalDuration,
                        polylinePoints: route.overview_polyline.points,
                        distance: { value: totalDistance, text: leg.distance.text },
                        duration: { value: totalDuration, text: leg.duration.text }
                    });
                } else {
                    console.log('Directions Error:', json.status, json.error_message);
                    reject(json.error_message || json.status);
                }
            })
            .catch(error => {
                console.log('getDirectionsApi Error:', error);
                reject("getDirectionsApi Call Error");
            });
    });
}
